import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, MessageSquare, MessageCircle, CheckCheck, XCircle, Clock, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';

/**
 * Full-conversation modal for a single installer. Merges outbound (Ox → them)
 * and inbound (them → Ox via Superchat webhook) rows into a chronological
 * thread, WhatsApp-style.
 *
 * Props: open, onClose, installerId, installerName
 */
export default function MessageThreadModal({ open, onClose, installerId, installerName }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !installerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/messages/thread?recipient_ref=${encodeURIComponent(installerId)}&limit=100`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) setMessages(data.messages || []);
      } catch (_e) {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, installerId]);

  useEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  if (!open) return null;

  const statusIcon = (status) => {
    if (status === 'delivered' || status === 'read') return <CheckCheck className="w-3 h-3" />;
    if (status === 'failed' || status === 'declined' || status === 'undelivered') return <XCircle className="w-3 h-3 text-red-300" />;
    if (status === 'received') return null;
    return <Clock className="w-3 h-3 opacity-60" />;
  };

  const channelIcon = (channel) => (
    channel?.includes('whatsapp')
      ? <MessageCircle className="w-3 h-3" />
      : <MessageSquare className="w-3 h-3" />
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="thread-modal">
      <div className="w-full max-w-2xl h-[80vh] flex flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-4 border-b bg-gray-50 rounded-t-2xl">
          <div>
            <h3 className="text-base font-bold text-gray-900">Conversation with {installerName || 'installer'}</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">Outbound + inbound merged · Superchat / Firetext audit trail</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="thread-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gradient-to-b from-gray-50 to-white">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading thread…
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MessageSquare className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No messages yet.</p>
              <p className="text-[11px] text-gray-400 mt-1">Once you send an SMS or WhatsApp, replies land back here automatically.</p>
            </div>
          ) : (
            messages.map((m) => {
              const inbound = m.direction === 'inbound';
              return (
                <div key={m.id || `${m.provider_id}-${m.created_at}`} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 shadow-sm ${
                      inbound
                        ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                        : (m.channel?.includes('whatsapp') ? 'bg-emerald-600 text-white rounded-br-none' : 'bg-blue-600 text-white rounded-br-none')
                    }`}
                  >
                    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold ${inbound ? 'text-gray-400' : 'text-white/80'}`}>
                      {inbound ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                      {channelIcon(m.channel)}
                      <span>{m.channel?.includes('whatsapp') ? 'WhatsApp' : 'SMS'}</span>
                    </div>
                    <p className="mt-0.5 text-sm whitespace-pre-wrap break-words">
                      {m.body || (m.template_id ? `[Template: ${m.template_id}]` : '')}
                    </p>
                    <div className={`flex items-center gap-1 mt-1 text-[10px] ${inbound ? 'text-gray-400' : 'text-white/80'} justify-end`}>
                      <span>{new Date(m.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      {!inbound && statusIcon(m.status)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 border-t bg-gray-50 rounded-b-2xl flex items-center justify-between">
          <p className="text-[11px] text-gray-500">Reply via the SMS Text or WhatsApp button on the installer's card.</p>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
